import React, { useState } from 'react';
import { XMarkIcon, ClipboardDocumentIcon, EyeIcon, EyeSlashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';

import GraphqlService from '../../service/graphqlService';
import { useSnackbar } from '../../providers/SnackbarContext';
import useAnalytics from '../../hooks/useAnalytics';

/**
 * RotateCredentialModal
 * FR020 #2 - Rotate credentials.
 * Two-step flow: (1) confirm rotation, (2) show-once display of the new
 * access key + secret access key (copy-to-clipboard + CSV download).
 * The secret is never persisted; it is cleared from state on close.
 */
const RotateCredentialModal = ({
  isOpen, onClose, onSuccess, credential,
}) => {
  const openSnackBar = useSnackbar();
  const { setAnalytics } = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);

  if (!isOpen) return null;

  const credentialName = credential?.description || credential?.logicalUser || credential?.id || 'credential';

  const clearSecrets = () => {
    setAccessKey('');
    setSecretKey('');
    setShowSecretKey(false);
    setShowSuccessView(false);
  };

  const handleConfirm = async () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-rotate-confirm' },
    });
    setIsLoading(true);
    try {
      const result = await GraphqlService.rotateCredential(credential.id);
      setAccessKey(result?.accessKey || '');
      setSecretKey(result?.secretAccessKey || '');
      setShowSuccessView(true);
      setIsLoading(false);
      if (typeof onSuccess === 'function') {
        onSuccess({ success: true, credential });
      }
    } catch (error) {
      console.error('Error rotating credential:', error);
      setIsLoading(false);
      openSnackBar(`Failed to rotate credential: ${error.message}`, 'error');
      if (typeof onSuccess === 'function') {
        onSuccess({ success: false, error: error.message });
      }
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      openSnackBar('Copied to clipboard!', 'success');
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
      openSnackBar('Failed to copy text to clipboard', 'error');
    });
  };

  const handleDownloadCsv = () => {
    const csvContent = `AccessKey,SecretKey\n${accessKey},${secretKey}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${credentialName}_rotated_credentials.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      openSnackBar('Failed to download CSV. Your browser might not support this feature.', 'error');
    }
  };

  const handleClose = () => {
    clearSecrets();
    onClose();
  };

  // Only fired from the step-1 "Cancel" button (before any rotation happened) so it's
  // distinguishable from closing the step-2 success view via the X icon.
  const handleCancel = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-rotate-cancel' },
    });
    handleClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-auto">
        {!showSuccessView ? (
          <>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Rotate Credentials</h2>
            <p className="mb-2 text-gray-700">
              You are about to rotate the credentials for
              {' '}
              <span className="font-semibold">{credentialName}</span>.
            </p>
            <p className="mb-6 text-sm text-gray-500">
              A new access key and secret will be generated. The secret is shown only once, so make sure to
              copy or download it. The old key can be removed afterwards once you confirm the new one works.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isLoading}
                className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isLoading ? 'Rotating…' : 'Rotate'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold text-gray-800">New Credentials</h2>
              <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="mb-4 text-sm text-red-600 font-medium">
              Copy or download these credentials now. The secret access key will not be shown again.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Access Key</label>
            <div className="flex items-center mb-4">
              <input
                type="text"
                readOnly
                value={accessKey}
                className="border border-gray-300 rounded-md px-3 py-2 w-full text-sm bg-gray-50"
              />
              <button
                type="button"
                onClick={() => handleCopyToClipboard(accessKey)}
                title="Copy access key"
                className="ml-2 text-gray-500 hover:text-indigo-600"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
              </button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Secret Access Key</label>
            <div className="flex items-center mb-6">
              <input
                type={showSecretKey ? 'text' : 'password'}
                readOnly
                value={secretKey}
                className="border border-gray-300 rounded-md px-3 py-2 w-full text-sm bg-gray-50"
              />
              <button
                type="button"
                onClick={() => setShowSecretKey((prev) => !prev)}
                title={showSecretKey ? 'Hide secret' : 'Show secret'}
                className="ml-2 text-gray-500 hover:text-indigo-600"
              >
                {showSecretKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={() => handleCopyToClipboard(secretKey)}
                title="Copy secret key"
                className="ml-2 text-gray-500 hover:text-indigo-600"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={handleDownloadCsv}
                className="inline-flex items-center px-4 py-2 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md focus:outline-none"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Download CSV
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RotateCredentialModal;
